import { useCallback } from "react"
import { observer } from "mobx-react-lite"

import { Input } from "@/components/input"
import { LcAddress } from "@/domains/core"
import { cn } from "@/domains/library/cn"
import { escapeRegExp } from "@/domains/library/escape-reg-ex"

// this is the token values that something needs to be compatible to use this input
export type ITokenInputValue = {
  address: LcAddress
  decimals: number
}

export const TokenInputValue = observer(
  ({
    className,
    disabled = false,
    placeholder = "0.0",
    readOnly = false,
    token,
    value,
    onBlur,
    onValueChange,
  }: {
    className?: string
    disabled?: boolean
    placeholder?: string
    readOnly?: boolean
    token: ITokenInputValue | null | undefined
    value: string
    onBlur?: () => void
    onValueChange: (value: string) => void
  }) => {
    const handleInputChanged = useCallback(
      (e: any) => {
        if (!onValueChange) return
        const val = e.target.value
        if (val === "" || /^\d*(?:\\[.])?\d*$/.test(escapeRegExp(val))) {
          // lets make sure they are not typing more than the allowed decimals.
          const split = val.split(".")

          if (split.length > 1 && split[1].length > (token?.decimals ?? 18)) {
            return
          }
          // if value is two chars long and the first char is a 0, only use the second char
          if (val.length === 2 && val[0] === "0" && val[1] !== ".") {
            onValueChange(val[1])
            return
          }
          onValueChange(val)
        }
      },
      [onValueChange, token?.address],
    )
    return (
      <Input
        id={token?.address.toViemAddress() ?? "unknown"}
        value={value}
        className={cn(
          "bg-transparent text-lg leading-6 shadow-none",
          "rounded-xl rounded-l-none border-0 border-transparent",
          "focus-within:border-0 focus-within:ring-0 focus:border-0 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0",
          className,
        )}
        onChange={handleInputChanged}
        onBlur={onBlur}
        readOnly={readOnly}
        disabled={disabled}
        placeholder={placeholder}
      />
    )
  },
)
